import React from 'react';
import { useNavigate } from 'react-router-dom';

export const BrandLogo = ({ className = '', collapsed, variant = 'dark-bg', size = 'md', fullLogo = false }) => {
  const navigate = useNavigate();
  const [logoFailed, setLogoFailed] = React.useState(false);

  React.useEffect(() => {
    setLogoFailed(false);
  }, [collapsed]);

  if (fullLogo || variant === 'full') {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => navigate('/dashboard')}
        onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard')}
        className={`flex items-center justify-center cursor-pointer select-none ${className}`}
        title="Go to Dashboard"
      >
        {!logoFailed ? (
          <img
            src="/images/logo/RSL_logo.png"
            className="h-16 md:h-20 max-w-full object-contain animate-fade-in"
            alt="Ratnakar Securities Limited"
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <div className="px-4 py-2 rounded-xl bg-gradient-to-br from-primary-500 to-red-600 shadow-md font-bold text-white text-lg select-none">
            RSL
          </div>
        )}
      </div>
    );
  }

  const isDarkBg = variant === 'dark-bg';
  const ratnakarColor = isDarkBg ? 'text-white' : 'text-slate-800 dark:text-white';
  const securitiesColor = isDarkBg ? 'text-[#00aeee]' : 'text-slate-600 dark:text-[#00aeee]';

  const imgSizeMap = {
    sm: 'h-[36px] w-[36px]',
    md: 'h-[46px] w-[46px]',
    lg: 'h-[68px] w-[68px]',
    xl: 'h-[90px] w-[90px]',
    '2xl': 'h-[110px] w-[110px]',
    '3xl': 'h-[135px] w-[135px]',
  };

  const titleSizeMap = {
    sm: 'text-[13px]',
    md: 'text-[16px]',
    lg: 'text-[24px]',
    xl: 'text-[32px]',
    '2xl': 'text-[40px]',
    '3xl': 'text-[48px]',
  };

  const subtitleSizeMap = {
    sm: 'text-[6.5px]',
    md: 'text-[7.5px]',
    lg: 'text-[11px]',
    xl: 'text-[14.5px]',
    '2xl': 'text-[18px]',
    '3xl': 'text-[22px]',
  };

  const imgClass = imgSizeMap[size] || imgSizeMap.md;
  const titleClass = titleSizeMap[size] || titleSizeMap.md;
  const subtitleClass = subtitleSizeMap[size] || subtitleSizeMap.md;

  if (logoFailed) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => navigate('/dashboard')}
        onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard')}
        className={`flex items-center gap-3 cursor-pointer select-none ${className}`}
        title="Go to Dashboard"
      >
        <div className={`flex items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-red-600 shadow-md shadow-primary-500/30 shrink-0 font-bold text-white select-none ${imgClass}`}>
          RSL
        </div>
        {!collapsed && (
          <div className="flex flex-col leading-tight text-left justify-center">
            <span className={`font-extrabold tracking-wider leading-none ${titleClass} ${ratnakarColor}`}>
              RATNAKAR
            </span>
            <span className={`font-bold tracking-[0.12em] mt-1 leading-none ${subtitleClass} ${isDarkBg ? 'text-slate-300' : 'text-slate-500 dark:text-slate-400'}`}>
              SECURITIES LIMITED
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate('/dashboard')}
      onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard')}
      className={`flex items-center gap-2.5 cursor-pointer select-none ${className}`}
      title="Go to Dashboard"
    >
      {/* Left Column: Red Boat Emblem (Bigger size to match tagline width) */}
      <div className="flex flex-col items-start shrink-0">
        <div className="h-[48px] w-[52px] overflow-hidden flex items-center justify-center rounded-sm">
          {!logoFailed ? (
            <img
              src="/images/logo/RSL_logo.png"
              className="h-full w-full object-cover object-left animate-fade-in"
              alt="Ratnakar Emblem"
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <div className="h-full w-full rounded bg-gradient-to-br from-red-600 to-blue-600 flex items-center justify-center text-white font-black text-xs shadow-sm">
              RSL
            </div>
          )}
        </div>
        <span className="text-[8px] font-medium tracking-[0.06em] text-slate-300 lowercase mt-0.5 leading-none opacity-90 whitespace-nowrap">
          rest assured
        </span>
      </div>

      {/* Right Column: Brand Text (RATNAKAR SECURITIES LIMITED) */}
      {!collapsed && (
        <div className="flex flex-col text-left justify-center select-none leading-none animate-fade-in pl-0.5">
          <span className="font-extrabold tracking-wider text-[15.5px] text-white leading-tight">
            RATNAKAR
          </span>
          <span className="font-bold tracking-[0.14em] text-[9.5px] text-[#00aeee] leading-tight mt-1">
            SECURITIES LIMITED
          </span>
        </div>
      )}
    </div>
  );
};

export default BrandLogo;
